import React from 'react';
import { Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Box, Typography, Chip } from '@mui/material';
import { TabPanel } from '@mui/lab';
import { SearchOutlined, TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import styled from '@emotion/styled';

const StyledTabPanel = styled(TabPanel)(({ theme }) => ({
  padding: theme.spacing(0),
}));

const LoadingBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(8),
}));

const SparklineRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(4),
  padding: theme.spacing(2),
  flexWrap: 'wrap',
}));

const SparklineGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const DividerCell = styled(TableCell)(({ theme }) => ({
  borderLeft: `1px solid ${theme.palette.divider}`,
}));

const ProfitCell = styled(TableCell, { shouldForwardProp: (prop) => prop !== 'negative' })(({ theme, negative }) => ({
  color: negative ? theme.palette.error.main : 'inherit',
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const fmt = (v) => `$ ${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TrendIcon = ({ delta }) => {
  if (delta > 1) return <TrendingUp fontSize="small" color="success" />;
  if (delta < -1) return <TrendingDown fontSize="small" color="error" />;
  return <TrendingFlat fontSize="small" color="disabled" />;
};

// Inline SVG sparkline of profit per month
const Sparkline = ({ values, width = 120, height = 32 }) => {
  if (!values || values.length === 0) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values.map((v, i) => `${i * step},${height - ((v - min) / range) * height}`).join(' ');
  return (
    <svg width={width} height={height} style={{ verticalAlign: 'middle' }}>
      <polyline fill="none" stroke="currentColor" strokeWidth="1.5" points={points} />
    </svg>
  );
};

const marginColor = (m) => m >= 30 ? 'success' : m >= 15 ? 'primary' : m >= 0 ? 'warning' : 'error';

const ProfitabilityTrendTable = ({ data, loading, hasFetched }) => {
  const expectedProfits = data.map((d) => d.expectedProfit);
  const actualProfits = data.map((d) => d.actualProfit);

  return (
    <StyledTabPanel value="trend">
      <Paper>
        <TableContainer>
          {loading ? (
            <LoadingBox><CircularProgress /></LoadingBox>
          ) : (
            <>
              {data.length > 1 && (
                <SparklineRow>
                  <SparklineGroup>
                    <Typography variant="body2" color="text.secondary">Expected profit:</Typography>
                    <Sparkline values={expectedProfits} />
                  </SparklineGroup>
                  <SparklineGroup>
                    <Typography variant="body2" color="text.secondary">Actual profit:</Typography>
                    <Sparkline values={actualProfits} />
                  </SparklineGroup>
                </SparklineRow>
              )}
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow key="header-group">
                    <TableCell rowSpan={2}>Month</TableCell>
                    <DividerCell align="center" colSpan={3}>Expected (billed)</DividerCell>
                    <DividerCell align="center" colSpan={3}>Actual (received)</DividerCell>
                    <DividerCell rowSpan={2} align="right">Labor Cost</DividerCell>
                    <TableCell rowSpan={2} align="right">Billable Hours</TableCell>
                  </TableRow>
                  <TableRow key="header">
                    <DividerCell align="right">Revenue</DividerCell>
                    <TableCell align="right">Profit</TableCell>
                    <TableCell align="right">Margin %</TableCell>
                    <DividerCell align="right">Revenue</DividerCell>
                    <TableCell align="right">Profit</TableCell>
                    <TableCell align="right">Margin %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, i) => {
                    const prev = i > 0 ? data[i - 1] : null;
                    const expectedDelta = prev ? row.expectedProfit - prev.expectedProfit : 0;
                    const actualDelta = prev ? row.actualProfit - prev.actualProfit : 0;
                    return (
                      <TableRow key={row.month} hover>
                        <TableCell>{row.month}</TableCell>
                        <DividerCell align="right">{fmt(row.expectedRevenue)}</DividerCell>
                        <ProfitCell align="right" negative={row.expectedProfit < 0}>
                          {fmt(row.expectedProfit)} {prev && <TrendIcon delta={expectedDelta} />}
                        </ProfitCell>
                        <TableCell align="right">
                          <Chip size="small" label={`${(row.expectedMargin || 0).toFixed(1)}%`} color={marginColor(row.expectedMargin)} />
                        </TableCell>
                        <DividerCell align="right">{fmt(row.actualRevenue)}</DividerCell>
                        <ProfitCell align="right" negative={row.actualProfit < 0}>
                          {fmt(row.actualProfit)} {prev && <TrendIcon delta={actualDelta} />}
                        </ProfitCell>
                        <TableCell align="right">
                          <Chip size="small" label={`${(row.actualMargin || 0).toFixed(1)}%`} color={marginColor(row.actualMargin)} />
                        </TableCell>
                        <DividerCell align="right">{fmt(row.laborCost)}</DividerCell>
                        <TableCell align="right">{(row.billableHours || 0).toLocaleString()}</TableCell>
                      </TableRow>
                    );
                  })}
                  {data.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <EmptyState>
                          <SearchOutlined />
                          <Typography variant="body1">{hasFetched ? 'No data for the selected range' : 'Click Generate Report to load data'}</Typography>
                        </EmptyState>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </TableContainer>
      </Paper>
    </StyledTabPanel>
  );
};

export default ProfitabilityTrendTable;
